import * as React from 'react';
import { FrontLayout } from 'client/ui/layout/FrontLayout';
import { Link, Redirect, RouteComponentProps } from 'react-router-dom';
import { Button, Card, Form, message, Result, Typography } from 'antd';
const { Title, Text, Paragraph } = Typography;

import { useApiQuery } from 'client/core/network/hooks/useApiQuery';
import { LoadingPage } from 'client/ui/loader/LoadingPage';
import { AlertPage } from 'client/ui/alert/AlertPage';
import { getNetworkErrorMessage } from 'client/core/network/errors/getNetworkErrorMessage';
import { useBreadcrumbItem } from 'client/core/router/breadcrumb/BreadcrumbContext';
import { Formik } from 'formik';
import { FormikForm } from 'client/ui/form/FormikForm';
import { TextInputItem } from 'client/ui/form/input/TextInputItem';
import { PreventNavigation } from 'client/routes/navigation/PreventNavigation';
import { LinkButton } from 'client/ui/link/LinkButton';
import { citizenResetValidator, ICitizenResetDto } from 'common/dto/CitizenDto';
import qs from 'qs';
import { schemaToValidator } from 'client/core/validation/schemaToValidator';
import { useApiMutation } from 'client/core/network/hooks/useApiMutation';
import { CitizenApi } from 'client/components/schema/citizen/CitizenApi';
import { useTranslation } from 'react-i18next';

interface Params {
  id: string;
}

export interface UserResetPasswordPageProps
  extends RouteComponentProps<Params> {}

/**
 * Pagina di reset della password
 */
export function UserResetPasswordPage(props: UserResetPasswordPageProps) {
  const { t } = useTranslation();

  useBreadcrumbItem({
    path: '/',
    title: t('procedures.breadcrumb', 'Istanze')
  });
  useBreadcrumbItem({
    path: props.location.pathname,
    title: t('user.password-reset.breadcrumb', 'Reimposta Password')
  });

  const token = qs.parse(props.location.search.substr(1)).token;

  const [reset, resetState] = useApiMutation(CitizenApi.reset, {});

  // Se non c'è nessun token, la pagina non ha senso.
  if (!token || typeof token !== 'string') {
    return <Redirect to="/" />;
  }

  const layout = {
    labelCol: { span: 6 },
    wrapperCol: { span: 12 }
  };
  const tailLayout = {
    wrapperCol: { sm: { offset: 6, span: 12 }, xs: { offset: 0, span: 24 } }
  };
  return (
    <FrontLayout>
      <div style={{ paddingTop: '20px', paddingBottom: '50px' }}>
        <Card>
          {resetState.response ? (
            <Result
              status="success"
              title={t(
                'user.password-reset.response.success.title',
                'Password reimpostata correttamente!'
              )}
              extra={
                <LinkButton
                  type="primary"
                  to="/utente/login"
                  children={t(
                    'user.password-reset.response.success.button-login',
                    'Effettua il Login'
                  )}
                />
              }
            />
          ) : (
            <Formik<ICitizenResetDto>
              enableReinitialize
              initialValues={{
                token,
                password: '',
                confirmPassword: ''
              }}
              validate={schemaToValidator(citizenResetValidator)}
              onSubmit={async rawValues => {
                try {
                  const input = await citizenResetValidator.validate(rawValues);
                  await reset({ data: { input } });
                } catch (e) {
                  console.error(e);
                  message.error(
                    t(
                      'user.password-reset.error.message',
                      'Non è stato possibile reimpostare la password.'
                    )
                  );
                }
              }}
            >
              {({ isSubmitting, dirty }) => (
                <>
                  <PreventNavigation
                    when={dirty && !isSubmitting}
                    message={t(
                      'user.registration.prevent-navigation-message',
                      `Ci sono dati non salvati, abbandonare la pagina? (Tutte le modifiche non salvate andranno perse)`
                    )}
                  />
                  <FormikForm {...layout}>
                    <Form.Item {...tailLayout}>
                      <Title level={3}>
                        {t('user.password-reset.title', 'Reimposta password')}
                      </Title>
                      <Paragraph>
                        {t(
                          'user.password-reset.text',
                          `Inserisci la nuova password per l'utente`
                        )}
                      </Paragraph>
                    </Form.Item>

                    <TextInputItem
                      type="password"
                      name="password"
                      label="Password"
                    />
                    <TextInputItem
                      type="password"
                      name="confirmPassword"
                      label={t(
                        'user.password-reset.form.password-confirm',
                        'Ripeti Password'
                      )}
                    />

                    <Form.Item {...tailLayout}>
                      <Button
                        htmlType="submit"
                        type="primary"
                        style={{ width: '100%' }}
                      >
                        {t(
                          'user.password-reset.form.submit-button',
                          'Reimposta Password'
                        )}
                      </Button>
                    </Form.Item>
                  </FormikForm>
                </>
              )}
            </Formik>
          )}
        </Card>
      </div>
    </FrontLayout>
  );
}
